import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { removeReviewItem, updateReviewItem, clearReviewQueue } from '../../store/slices/reviewQueueSlice';
import { addOrder } from '../../store/slices/ordersSlice';
import { addClient } from '../../store/slices/clientsSlice';
import { ReviewQueueItem, FlagReason, ParsedOrderData } from '../../types/reviewQueue';
import { v4 as uuidv4 } from 'uuid';
import { addToast } from '../../store/slices/toastsSlice';
import {
  findOrPrepareClient,
  createClientFromParsedData,
  createOrderFromParsedData,
  convertToReviewQueueData
} from '../../utils/emailOrderProcessor';
import { parseOrderEmail } from '../../utils/emailParser';
import { EmailData } from '../../utils/emailFetcher';

interface ReviewQueueModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableRecipes: { id: string; name: string }[];
}

const getFlagReasonLabel = (reason: FlagReason): string => {
  const labels: Record<FlagReason, string> = {
    'missing-address': 'Missing Address',
    duplicate: 'Possible Duplicate',
    'parse-error': 'Parse Error',
    'unusual-format': 'Unusual Format',
    'missing-customer-name': 'Missing Customer Name',
    'missing-items': 'Missing Items',
    'invalid-email': 'Invalid Email',
    'invalid-phone': 'Invalid Phone',
    'unmatched-recipe': 'Unmatched Recipe'
  };
  return labels[reason] || reason;
};

const getFlagReasonColor = (reason: FlagReason): string => {
  const colors: Record<FlagReason, string> = {
    'missing-address': '#FF6B35',
    duplicate: '#FFA500',
    'parse-error': '#DC3545',
    'unusual-format': '#17A2B8',
    'missing-customer-name': '#FF6B35',
    'missing-items': '#FF6B35',
    'invalid-email': '#DC3545',
    'invalid-phone': '#DC3545',
    'unmatched-recipe': '#9C27B0'
  };
  return colors[reason] || '#666';
};

const ReviewQueueModal: React.FC<ReviewQueueModalProps> = ({ isOpen, onClose, availableRecipes }) => {
  const dispatch = useAppDispatch();
  const reviewQueueItems = useAppSelector(state => state.reviewQueue.items);
  const clients = useAppSelector(state => state.clients.clients);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<ParsedOrderData | null>(null);
  const [showRawEmail, setShowRawEmail] = useState<string | null>(null);

  if (!isOpen) return null;

  const selectedItem = selectedItemId ? reviewQueueItems.find(item => item.id === selectedItemId) : null;

  /**
   * Re-validates edited data and updates flag reason/details if the error has been fixed
   */
  const revalidateEditedItem = (item: ReviewQueueItem, editedData: ParsedOrderData): ReviewQueueItem => {
    const currentFlagReason = item.flagReason;
    let newFlagReason = currentFlagReason;
    let newFlagDetails = item.flagDetails;

    // Check if the original flag reason has been resolved
    switch (currentFlagReason) {
      case 'missing-customer-name':
        if (editedData.customerName && editedData.customerName.trim() !== '') {
          // Customer name is now present, check if there are other issues
          const otherIssues = findNextValidationIssue(editedData, 'missing-customer-name');
          if (otherIssues) {
            newFlagReason = otherIssues.flagReason;
            newFlagDetails = otherIssues.flagDetails;
          } else {
            // No more issues - this shouldn't happen if we still have the item in review queue
            // but we'll keep it in the queue with updated data
            newFlagReason = currentFlagReason;
            newFlagDetails = 'Data updated - ready for approval';
          }
        }
        break;

      case 'invalid-email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (editedData.email && emailRegex.test(editedData.email)) {
          const otherIssues = findNextValidationIssue(editedData, 'invalid-email');
          if (otherIssues) {
            newFlagReason = otherIssues.flagReason;
            newFlagDetails = otherIssues.flagDetails;
          } else {
            newFlagDetails = 'Data updated - ready for approval';
          }
        }
        break;

      case 'invalid-phone':
        if (editedData.phone && /\d{3,}/.test(editedData.phone)) {
          const otherIssues = findNextValidationIssue(editedData, 'invalid-phone');
          if (otherIssues) {
            newFlagReason = otherIssues.flagReason;
            newFlagDetails = otherIssues.flagDetails;
          } else {
            newFlagDetails = 'Data updated - ready for approval';
          }
        }
        break;

      case 'missing-address':
        const hasStreet = editedData.streetAddress && editedData.streetAddress.trim() !== '';
        const hasCity = editedData.city && editedData.city.trim() !== '';
        const hasProvince = editedData.province && editedData.province.trim() !== '';
        const hasPostalCode = editedData.postalCode && editedData.postalCode.trim() !== '';

        if (hasStreet && hasCity && hasProvince && hasPostalCode) {
          const otherIssues = findNextValidationIssue(editedData, 'missing-address');
          if (otherIssues) {
            newFlagReason = otherIssues.flagReason;
            newFlagDetails = otherIssues.flagDetails;
          } else {
            newFlagDetails = 'Data updated - ready for approval';
          }
        }
        break;

      case 'missing-items':
        if (editedData.items && editedData.items.length > 0) {
          const otherIssues = findNextValidationIssue(editedData, 'missing-items');
          if (otherIssues) {
            newFlagReason = otherIssues.flagReason;
            newFlagDetails = otherIssues.flagDetails;
          } else {
            newFlagDetails = 'Data updated - ready for approval';
          }
        }
        break;

      // For duplicate and other reasons, we don't automatically change the flag
      default:
        break;
    }

    return {
      ...item,
      parsedData: editedData,
      flagReason: newFlagReason,
      flagDetails: newFlagDetails
    };
  };

  /**
   * Finds the next validation issue in the edited data, excluding the specified issue
   */
  const findNextValidationIssue = (
    data: ParsedOrderData,
    excludeReason: FlagReason
  ): { flagReason: FlagReason; flagDetails: string } | null => {
    // Check in order of validation priority

    if (excludeReason !== 'missing-customer-name') {
      if (!data.customerName || data.customerName.trim() === '') {
        return {
          flagReason: 'missing-customer-name',
          flagDetails: 'Customer name is required but was not found in the email'
        };
      }
    }

    if (excludeReason !== 'invalid-email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!data.email || !emailRegex.test(data.email)) {
        return {
          flagReason: 'invalid-email',
          flagDetails: `Email "${data.email}" is not valid or is missing`
        };
      }
    }

    if (excludeReason !== 'invalid-phone') {
      if (!data.phone || !/\d{3,}/.test(data.phone)) {
        return {
          flagReason: 'invalid-phone',
          flagDetails: `Phone "${data.phone}" is not valid or is missing`
        };
      }
    }

    if (excludeReason !== 'missing-address') {
      if (!data.streetAddress || data.streetAddress.trim() === '') {
        return {
          flagReason: 'missing-address',
          flagDetails: 'Street address is required but was not found'
        };
      }
      if (!data.city || data.city.trim() === '') {
        return {
          flagReason: 'missing-address',
          flagDetails: 'City is required but was not found'
        };
      }
      if (!data.province || data.province.trim() === '') {
        return {
          flagReason: 'missing-address',
          flagDetails: 'Province is required but was not found'
        };
      }
      if (!data.postalCode || data.postalCode.trim() === '') {
        return {
          flagReason: 'missing-address',
          flagDetails: 'Postal code is required but was not found'
        };
      }
    }

    if (excludeReason !== 'missing-items') {
      if (!data.items || data.items.length === 0) {
        return {
          flagReason: 'missing-items',
          flagDetails: 'At least one order item is required'
        };
      }
    }

    return null;
  };

  const handleEdit = (item: ReviewQueueItem) => {
    setSelectedItemId(item.id);
    setEditingData({ ...item.parsedData });
  };

  const handleCancelEdit = () => {
    setSelectedItemId(null);
    setEditingData(null);
  };

  const handleSaveEdit = () => {
    if (selectedItemId && editingData) {
      const currentItem = reviewQueueItems.find(item => item.id === selectedItemId);

      if (currentItem) {
        // Re-validate the edited data to check if the flag reason should be updated
        const updatedItem = revalidateEditedItem(currentItem, editingData);

        // Update the item with new data and potentially updated flag reason/details
        dispatch(
          updateReviewItem({
            id: selectedItemId,
            parsedData: editingData,
            flagReason: updatedItem.flagReason,
            flagDetails: updatedItem.flagDetails
          })
        );

        dispatch(addToast('Review item updated', 'success'));
      } else {
        // Fallback if item not found
        dispatch(updateReviewItem({ id: selectedItemId, parsedData: editingData }));
        dispatch(addToast('Review item updated', 'success'));
      }

      setSelectedItemId(null);
      setEditingData(null);
    }
  };

  const handleApprove = (item: ReviewQueueItem, dataToUse: ParsedOrderData) => {
    // Find or create client using utility function
    const clientResult = findOrPrepareClient(dataToUse.email, dataToUse.phone, clients);

    // If client doesn't exist, create it
    if (!clientResult.isExisting) {
      const newClient = createClientFromParsedData(dataToUse, clientResult.clientId);
      dispatch(addClient(newClient));
      console.log(`[ReviewQueue] ✓ Created new client: ${newClient.name}`);
    } else {
      console.log(`[ReviewQueue] ✓ Using existing client: ${clientResult.existingClient?.id}`);
    }

    // Create order using utility function, including original email text
    const newOrder = createOrderFromParsedData(
      dataToUse,
      clientResult.clientId,
      availableRecipes,
      item.originalOrderText
    );

    dispatch(addOrder(newOrder));
    dispatch(removeReviewItem(item.id));
    dispatch(
      addToast(
        `Order created for ${dataToUse.customerName}${!clientResult.isExisting ? ' (new client)' : ''}`,
        'success'
      )
    );

    // Clear editing state if this was the item being edited
    if (selectedItemId === item.id) {
      setSelectedItemId(null);
      setEditingData(null);
    }
  };

  const handleDismiss = (itemId: string) => {
    if (
      window.confirm(
        'Are you sure you want to dismiss this item? It will be permanently removed from the review queue.'
      )
    ) {
      dispatch(removeReviewItem(itemId));
      dispatch(addToast('Review item dismissed', 'info'));

      if (selectedItemId === itemId) {
        setSelectedItemId(null);
        setEditingData(null);
      }
    }
  };

  const handleRetryAll = () => {
    if (reviewQueueItems.length === 0) {
      return;
    }

    if (
      !window.confirm(
        `Re-parse all ${reviewQueueItems.length} item${reviewQueueItems.length !== 1 ? 's' : ''} using the updated parsing logic? This will update the parsed data for each item while preserving the original raw email.`
      )
    ) {
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    reviewQueueItems.forEach(item => {
      try {
        // Reconstruct the EmailData object from the raw email
        const emailData: EmailData = {
          subject: 'Order',
          from: item.parsedData.email,
          replyTo: item.parsedData.email,
          body: item.rawEmail,
          date: new Date(item.receivedAt),
          receivedAt: item.receivedAt
        };

        // Re-parse the email
        const parsedEmail = parseOrderEmail(emailData, availableRecipes);

        // Convert to ParsedOrderData format
        const updatedParsedData = convertToReviewQueueData(parsedEmail);

        // Update the item in the store
        dispatch(updateReviewItem({ id: item.id, parsedData: updatedParsedData }));
        successCount++;
      } catch (error) {
        console.error(`[ReviewQueue] Failed to re-parse item ${item.id}:`, error);
        errorCount++;
      }
    });

    // Show results
    if (errorCount === 0) {
      dispatch(addToast(`Successfully re-parsed all ${successCount} items`, 'success'));
    } else {
      dispatch(
        addToast(`Re-parsed ${successCount} items with ${errorCount} error${errorCount !== 1 ? 's' : ''}`, 'warning')
      );
    }

    // Clear editing state if currently editing
    setSelectedItemId(null);
    setEditingData(null);
  };

  const handleClearAll = () => {
    if (reviewQueueItems.length === 0) {
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to clear all ${reviewQueueItems.length} item${reviewQueueItems.length !== 1 ? 's' : ''} from the review queue? This action cannot be undone. The original emails in your inbox will remain untouched.`
      )
    ) {
      return;
    }

    dispatch(clearReviewQueue());
    dispatch(addToast('Review queue cleared', 'info'));

    // Clear editing state
    setSelectedItemId(null);
    setEditingData(null);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderEditForm = (data: ParsedOrderData, item: ReviewQueueItem) => {
    if (!editingData) return null;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '0.9rem' }}>
            Customer Name
          </label>
          <input
            type="text"
            value={editingData.customerName}
            onChange={e => setEditingData({ ...editingData, customerName: e.target.value })}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.95rem'
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '0.9rem' }}>
              Email
            </label>
            <input
              type="email"
              value={editingData.email}
              onChange={e => setEditingData({ ...editingData, email: e.target.value })}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.95rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '0.9rem' }}>
              Phone
            </label>
            <input
              type="tel"
              value={editingData.phone}
              onChange={e => setEditingData({ ...editingData, phone: e.target.value })}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.95rem'
              }}
            />
          </div>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '0.9rem' }}>
            Street Address
          </label>
          <input
            type="text"
            value={editingData.streetAddress}
            onChange={e => setEditingData({ ...editingData, streetAddress: e.target.value })}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.95rem'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '0.9rem' }}>
            Address Line 2
          </label>
          <input
            type="text"
            value={editingData.addressLine2}
            onChange={e => setEditingData({ ...editingData, addressLine2: e.target.value })}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.95rem'
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '0.9rem' }}>City</label>
            <input
              type="text"
              value={editingData.city}
              onChange={e => setEditingData({ ...editingData, city: e.target.value })}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.95rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '0.9rem' }}>
              Province
            </label>
            <input
              type="text"
              value={editingData.province}
              onChange={e => setEditingData({ ...editingData, province: e.target.value })}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.95rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '0.9rem' }}>
              Postal Code
            </label>
            <input
              type="text"
              value={editingData.postalCode}
              onChange={e => setEditingData({ ...editingData, postalCode: e.target.value })}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.95rem'
              }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '0.9rem' }}>
              Order Date
            </label>
            <input
              type="date"
              value={editingData.orderDate}
              onChange={e => setEditingData({ ...editingData, orderDate: e.target.value })}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.95rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '0.9rem' }}>
              Delivery Type
            </label>
            <select
              value={editingData.deliveryType}
              onChange={e => setEditingData({ ...editingData, deliveryType: e.target.value as 'Pickup' | 'Delivery' })}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.95rem'
              }}
            >
              <option value="Delivery">Delivery</option>
              <option value="Pickup">Pickup</option>
            </select>
          </div>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>Items</label>
          {editingData.items.map((item, index) => (
            <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
              <input
                type="text"
                value={item.recipeName}
                onChange={e => {
                  const newItems = [...editingData.items];
                  newItems[index] = { ...item, recipeName: e.target.value };
                  setEditingData({ ...editingData, items: newItems });
                }}
                placeholder="Recipe Name"
                style={{
                  flex: 2,
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '0.95rem'
                }}
              />
              <input
                type="number"
                value={item.quantity}
                onChange={e => {
                  const newItems = [...editingData.items];
                  newItems[index] = { ...item, quantity: parseInt(e.target.value) || 0 };
                  setEditingData({ ...editingData, items: newItems });
                }}
                placeholder="Qty"
                min="1"
                style={{
                  flex: 1,
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '0.95rem'
                }}
              />
              <button
                onClick={() => {
                  const newItems = editingData.items.filter((_, i) => i !== index);
                  setEditingData({ ...editingData, items: newItems });
                }}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            onClick={() => {
              setEditingData({
                ...editingData,
                items: [...editingData.items, { recipeName: '', quantity: 1 }]
              });
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              marginTop: '8px'
            }}
          >
            + Add Item
          </button>
        </div>

        <div>
          <button
            onClick={() => setShowRawEmail(showRawEmail === item.id ? null : item.id)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            {showRawEmail === item.id ? 'Hide' : 'Show'} Raw Email
          </button>

          {showRawEmail === item.id && (
            <div
              style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                maxHeight: '200px',
                overflowY: 'auto'
              }}
            >
              <pre
                style={{
                  margin: 0,
                  fontSize: '0.85rem',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word'
                }}
              >
                {item.rawEmail}
              </pre>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button
            onClick={handleCancelEdit}
            style={{
              padding: '10px 20px',
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveEdit}
            style={{
              padding: '10px 20px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600'
            }}
          >
            Save Changes
          </button>
        </div>
      </div>
    );
  };

  const renderItemPreview = (item: ReviewQueueItem) => {
    const data = item.parsedData;
    const isEditing = selectedItemId === item.id;

    return (
      <div
        key={item.id}
        style={{
          padding: '20px',
          backgroundColor: '#fff',
          border: '2px solid #e0e0e0',
          borderRadius: '8px',
          marginBottom: '16px'
        }}
      >
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}
        >
          <div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem', color: '#333' }}>
              {data.customerName || '<No Name>'}
            </h3>
            <span
              style={{
                display: 'inline-block',
                padding: '4px 12px',
                backgroundColor: getFlagReasonColor(item.flagReason),
                color: 'white',
                borderRadius: '4px',
                fontSize: '0.85rem',
                fontWeight: '600'
              }}
            >
              {getFlagReasonLabel(item.flagReason)}
            </span>
            {item.flagDetails && (
              <p style={{ margin: '8px 0 0 0', fontSize: '0.9rem', color: '#666' }}>{item.flagDetails}</p>
            )}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#999', textAlign: 'right' }}>
            <div>Received: {new Date(item.receivedAt).toLocaleString()}</div>
            <div>Flagged: {new Date(item.flaggedAt).toLocaleString()}</div>
          </div>
        </div>

        {isEditing ? (
          renderEditForm(data, item)
        ) : (
          <>
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '0.95rem', color: '#666' }}>Contact Information</h4>
              <div style={{ fontSize: '0.9rem', color: '#333' }}>
                <div>
                  <strong>Email:</strong> {data.email || '<Missing>'}
                </div>
                <div>
                  <strong>Phone:</strong> {data.phone || '<Missing>'}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '0.95rem', color: '#666' }}>Delivery Address</h4>
              <div style={{ fontSize: '0.9rem', color: '#333' }}>
                <div>{data.streetAddress || '<Missing>'}</div>
                {data.addressLine2 && <div>{data.addressLine2}</div>}
                <div>
                  {data.city || '<Missing>'}, {data.province || '<Missing>'} {data.postalCode || '<Missing>'}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '0.95rem', color: '#666' }}>Order Details</h4>
              <div style={{ fontSize: '0.9rem', color: '#333' }}>
                <div>
                  <strong>Date:</strong> {data.orderDate}
                </div>
                <div>
                  <strong>Delivery Type:</strong> {data.deliveryType}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '0.95rem', color: '#666' }}>Items</h4>
              {data.items.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.9rem' }}>
                  {data.items.map((item, index) => (
                    <li key={index}>
                      {item.quantity}x {item.recipeName}
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ fontSize: '0.9rem', color: '#999' }}>No items found</div>
              )}
            </div>

            {item.originalOrderText && (
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '0.95rem', color: '#666' }}>Original Order Text</h4>
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#f9f9f9',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    color: '#555',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    maxHeight: '150px',
                    overflowY: 'auto'
                  }}
                >
                  {item.originalOrderText}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                onClick={() => setShowRawEmail(showRawEmail === item.id ? null : item.id)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                {showRawEmail === item.id ? 'Hide' : 'Show'} Raw Email
              </button>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => handleEdit(item)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: '600'
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleApprove(item, data)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: '600'
                  }}
                >
                  Approve & Create Order
                </button>
                <button
                  onClick={() => handleDismiss(item.id)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: '600'
                  }}
                >
                  Dismiss
                </button>
              </div>
            </div>

            {showRawEmail === item.id && (
              <div
                style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontSize: '0.85rem',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word'
                  }}
                >
                  {item.rawEmail}
                </pre>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          width: '100%',
          maxWidth: '900px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div
          style={{
            padding: '24px',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '1.5rem', color: '#333' }}>Review Queue</h2>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
              {reviewQueueItems.length} item{reviewQueueItems.length !== 1 ? 's' : ''} pending review
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {reviewQueueItems.length > 0 && (
              <>
                <button
                  onClick={handleRetryAll}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#FF9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: '600'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = '#F57C00';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = '#FF9800';
                  }}
                >
                  Retry All
                </button>
                <button
                  onClick={handleClearAll}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: '600'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = '#d32f2f';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = '#f44336';
                  }}
                >
                  Clear All
                </button>
              </>
            )}
            <button
              onClick={onClose}
              style={{
                width: '36px',
                height: '36px',
                border: 'none',
                backgroundColor: 'transparent',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#999',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
                e.currentTarget.style.color = '#333';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#999';
              }}
            >
              ×
            </button>
          </div>
        </div>

        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {reviewQueueItems.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#999'
              }}
            >
              <p style={{ fontSize: '1.1rem', margin: 0 }}>No items in review queue</p>
              <p style={{ fontSize: '0.9rem', margin: '8px 0 0 0' }}>
                Flagged emails will appear here for manual review
              </p>
            </div>
          ) : (
            reviewQueueItems.map(item => renderItemPreview(item))
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewQueueModal;
